import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Container, Box, Typography, Avatar, Chip, CircularProgress } from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LinkIcon from "@mui/icons-material/Link";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import IconButton from "@mui/material/IconButton";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

const DEFAULT_AVATAR = `data:image/svg+xml;charset=UTF-8,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="32" fill="#161b2e"/><circle cx="32" cy="24" r="11" fill="#2a3a5c"/><ellipse cx="32" cy="54" rx="18" ry="12" fill="#2a3a5c"/></svg>')}`;

const PublicProfile = () => {
  const { uid } = useParams();
  const navigate = useNavigate();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const snap = await getDoc(doc(db, "users", uid));
        if (snap.exists()) setUserData(snap.data());
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetchUser();
  }, [uid]);

  const glass = {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "20px",
    backdropFilter: "blur(12px)",
    padding: "20px",
    marginBottom: "16px",
  };

  const infoRow = (icon, text) =>
    text ? (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.2, mb: 1 }}>
        <Box sx={{ color: "#00d4ff", display: "flex" }}>{icon}</Box>
        <Typography sx={{ fontSize: "0.9rem", color: "rgba(200,215,255,0.75)" }}>{text}</Typography>
      </Box>
    ) : null;

  return (
    <Box sx={{ minHeight: "100vh", background: "#080b14" }}>
      {/* AppBar */}
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          height: "68px",
          background: "rgba(8,11,20,0.88)",
          backdropFilter: "blur(24px)",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
          display: "flex",
          alignItems: "center",
          px: 2,
          zIndex: 100,
        }}
      >
        <IconButton onClick={() => navigate(-1)} sx={{ color: "#00d4ff", mr: 1 }}>
          <ArrowBackIosNewIcon fontSize="small" />
        </IconButton>
        <Typography sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#e6edf3" }}>
          Perfil profesional
        </Typography>
      </Box>

      <Container sx={{ mt: "84px", pb: 6, px: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : userData ? (
          <>
            {/* Hero */}
            <Box sx={{ ...glass, display: "flex", flexDirection: "column", alignItems: "center", pt: 3, pb: 2.5 }}>
              <Avatar
                src={userData.profileImageUrl || DEFAULT_AVATAR}
                alt={userData.displayName}
                sx={{
                  width: 96,
                  height: 96,
                  border: "3px solid rgba(0,212,255,0.45)",
                  boxShadow: "0 0 28px rgba(0,212,255,0.25)",
                  mb: 1.5,
                  borderRadius: "24px",
                }}
              />
              <Typography sx={{ fontWeight: 800, fontSize: "1.4rem", color: "#e6edf3", letterSpacing: "-0.3px" }}>
                {userData.displayName}
              </Typography>
              {userData.profession && (
                <Chip
                  label={userData.profession}
                  size="small"
                  sx={{ mt: 0.8, background: "rgba(0,212,255,0.12)", border: "1px solid rgba(0,212,255,0.3)", color: "#00d4ff", fontWeight: 600, fontSize: "0.78rem" }}
                />
              )}
              {userData.experience && (
                <Typography sx={{ mt: 0.8, fontSize: "0.75rem", color: "rgba(255,255,255,0.35)", letterSpacing: "1px", textTransform: "uppercase" }}>
                  {userData.experience} de experiencia
                </Typography>
              )}
            </Box>

            {/* Contacto */}
            <Box sx={glass}>
              <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.3)", letterSpacing: "2px", textTransform: "uppercase", mb: 1.5 }}>
                Contacto
              </Typography>
              {infoRow(<PhoneIcon fontSize="small" />, userData.phone)}
              {infoRow(<EmailIcon fontSize="small" />, userData.email)}
              {infoRow(<LocationOnIcon fontSize="small" />, userData.point)}
              {infoRow(<LinkIcon fontSize="small" />, userData.website)}
              {infoRow(<LinkedInIcon fontSize="small" />, userData.linkedin)}
            </Box>

            {/* Bio / Curriculum */}
            {userData.bio && (
              <Box sx={glass}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                  <DescriptionIcon sx={{ color: "#00d4ff", fontSize: 18 }} />
                  <Typography sx={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.3)", letterSpacing: "2px", textTransform: "uppercase" }}>
                    Curriculum / Bio
                  </Typography>
                </Box>
                <Typography sx={{ fontSize: "0.92rem", color: "rgba(200,215,255,0.75)", lineHeight: 1.7, whiteSpace: "pre-wrap" }}>
                  {userData.bio}
                </Typography>
              </Box>
            )}
          </>
        ) : (
          <Typography sx={{ color: "rgba(255,255,255,0.4)", textAlign: "center", mt: 8 }}>
            Usuario no encontrado
          </Typography>
        )}
      </Container>
    </Box>
  );
};

export default PublicProfile;
